import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fade,
  Zoom,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Assessment,
  Assignment,
  Settings,
  Description,
  Search,
  Engineering,
  QrCode2,
  PersonAdd,
  Lock,
  Build,
  Security,
  ArrowForward
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/Layout/PageLayout';
import { useTranslation } from '../hooks/useTranslation';
import { useRole } from '../hooks/useRole';

interface MenuCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  color: string;
  gradient: string;
  requiredRole?: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const { t } = useTranslation();
  const { roleId, roleDisplayName, isAdmin, isAdminOrManager } = useRole();

  const userRole = roleId || '3';

  const menuCards: MenuCard[] = [
    {
      id: 'stage2',
      title: t('navigation.rfidManagement'),
      description: t('pages.rfidManagement.description'),
      icon: <QrCode2 sx={{ fontSize: 40 }} />,
      route: '/rfid',
      color: '#1976d2',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      id: 'slump',
      title: t('navigation.uSlump'),
      description: t('pages.siteSlump.description'),
      icon: <Assessment sx={{ fontSize: 40 }} />,
      route: '/site-slump',
      color: '#2e7d32',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      id: 'contract',
      title: t('navigation.contractData'),
      description: t('pages.contractData.description'),
      icon: <Settings sx={{ fontSize: 40 }} />,
      route: '/contract-data',
      color: '#ed6c02',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      //requiredRole: '1,2'
    },
    // {
    //   id: 'grout',
    //   title: 'Grout Record',
    //   description: 'Grout 記錄管理',
    //   icon: <Engineering sx={{ fontSize: 40 }} />,
    //   route: '/grout',
    //   color: '#9c27b0',
    //   gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    // },
    {
      id: 'mixid',
      title: t('navigation.mixID'),
      description: t('pages.mixID.description'),
      icon: <Description sx={{ fontSize: 40 }} />,
      route: '/mixid',
      color: '#d32f2f',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    // {
    //   id: 'daily-report',
    //   title: t('navigation.daily'),
    //   description: t('pages.dailyReport.description'),
    //   icon: <Assignment sx={{ fontSize: 40 }} />,
    //   route: '/daily-report',
    //   color: '#388e3c',
    //   gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    // },
    {
      id: 'first3-end3',
      title: t('navigation.first3End3'),
      description: '前3後3 數據查詢',
      icon: <Search sx={{ fontSize: 40 }} />,
      route: '/first3-end3',
      color: '#f57c00',
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    },
    {
      id: 'settings',
      title: '系統設定',
      description: '管理系統用戶、權限和配置設定',
      icon: <Settings sx={{ fontSize: 40 }} />,
      route: '/settings',
      color: '#7b1fa2',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      requiredRole: '1,2'
    },
  ];

  // 動畫效果：卡片依次出現
  useEffect(() => {
    const timer = setTimeout(() => {
      menuCards.forEach((card, index) => {
        if (hasPermission(card.requiredRole)) {
          setTimeout(() => {
            setVisibleCards(prev => new Set([...prev, card.id]));
          }, index * 100);
        }
      });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const hasPermission = (requiredRole?: string): boolean => {
    if (!requiredRole) return true;
    const allowedRoles = requiredRole.split(',');
    return allowedRoles.includes(userRole);
  };

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <PageLayout title='Home'>
      {/* 英雄區域 */}
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: 2,
        mb: 6,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                {t('navigation.hkhaRFIDManagementSystem')}
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                {t('pages.home.description')}
              </Typography>
              {user && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <Security />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {t('pages.home.welcome')} {user.nickName}
                    </Typography>
                    <Chip
                      label={roleDisplayName}
                      color="primary"
                      variant="outlined"
                      sx={{ color: 'white', borderColor: 'white' }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* 用戶管理功能 */}
      {/* {isAdminOrManager && (
        <Fade in timeout={1600}>
          <Paper sx={{
            p: 4,
            mb: 6,
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" color="#4d1300" sx={{ mb: 3 }}>
              用戶管理
            </Typography>
            <Grid container spacing={3}>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setShowCreateAccount(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    }
                  }}
                >
                  創建新帳戶
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<Lock />}
                  onClick={() => setShowChangePassword(true)}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      borderColor: '#764ba2',
                      backgroundColor: 'rgba(102, 126, 234, 0.04)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  修改密碼
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Fade>
      )} */}

      {/* 主要功能模組 */}
      <Grid container spacing={3}>
        {menuCards.map((card, index) => {
          if (!hasPermission(card.requiredRole)) return null;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
              <Zoom in={visibleCards.has(card.id)} timeout={1000 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'white',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      '& .card-icon': {
                        transform: 'scale(1.15) rotate(8deg)',
                      },
                      '& .card-arrow': {
                        opacity: 1,
                        transform: 'translateX(0)',
                      }
                    },
                  }}
                  onClick={() => handleCardClick(card.route)}
                >
                  {/* 背景裝飾 */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: card.gradient,
                    opacity: 0.03,
                    zIndex: 0
                  }} />

                  {/* 圖標區域 */}
                  <Box
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      background: card.gradient,
                      color: 'white',
                      minHeight: 160,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* 背景裝飾圓圈 */}
                    <Box sx={{
                      position: 'absolute',
                      top: -60,
                      right: -60,
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.1)',
                      opacity: 0.6
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      bottom: -40,
                      left: -40,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.05)',
                      opacity: 0.8
                    }} />

                    {/* 圖標 */}
                    <Box className="card-icon" sx={{
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 1,
                      position: 'relative'
                    }}>
                      {card.icon}
                    </Box>

                    {/* 懸停時的箭頭 */}
                    <Box className="card-arrow" sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      opacity: 0,
                      transform: 'translateX(-10px)',
                      transition: 'all 0.3s ease',
                      zIndex: 1
                    }}>
                      <ArrowForward sx={{ fontSize: 24 }} />
                    </Box>
                  </Box>

                  {/* 內容區域 */}
                  <CardContent sx={{
                    flexGrow: 1,
                    textAlign: 'center',
                    p: 3,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold" color="#4d1300" sx={{ mb: 1 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          );
        })}
      </Grid>

      {/* 系統說明 */}
      <Fade in timeout={2000}>
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Paper elevation={0} sx={{
            p: 6,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" color="#4d1300">
              {t('pages.home.systemDescription')}
            </Typography>
            <Divider sx={{ my: 3, mx: 'auto', width: 100 }} />
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              {t('pages.home.systemDescriptionContent')}
            </Typography>
          </Paper>
        </Box>
      </Fade>

      {/* 創建帳戶對話框 */}
      <Dialog open={showCreateAccount} onClose={() => setShowCreateAccount(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          創建新帳戶
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="用戶ID"
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="密碼"
                type="password"
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="確認密碼"
                type="password"
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="暱稱"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>角色</InputLabel>
                <Select label="角色" sx={{ borderRadius: 2 }}>
                  <MenuItem value="1">管理員</MenuItem>
                  <MenuItem value="2">主管</MenuItem>
                  <MenuItem value="3">一般用戶</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowCreateAccount(false)}
            sx={{ borderRadius: 2, px: 3 }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              showSnackbar('帳戶創建功能待實現', 'info');
              setShowCreateAccount(false);
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            創建
          </Button>
        </DialogActions>
      </Dialog>

      {/* 修改密碼對話框 */}
      <Dialog open={showChangePassword} onClose={() => setShowChangePassword(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          修改密碼
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="當前密碼"
                type="password"
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="新密碼"
                type="password"
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="確認新密碼"
                type="password"
                required
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowChangePassword(false)}
            sx={{ borderRadius: 2, px: 3 }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              showSnackbar('密碼修改功能待實現', 'info');
              setShowChangePassword(false);
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            修改
          </Button>
        </DialogActions>
      </Dialog>

      {/* 通知 */}
      <Alert
        severity={snackbar.severity}
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          display: snackbar.open ? 'flex' : 'none',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        {snackbar.message}
      </Alert>
    </PageLayout>
  );
};

export default Home;